import { useState, useEffect } from "react";
import "./CreatePurchase.css";
import dataService from "../services/dataService";
import { useNavigate, useParams } from "react-router-dom";
import SearchableSelect from "../components/SearchableSelect";
import { useToast } from "../context/ToastContext";

function CreatePurchase() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vendRes, prodRes] = await Promise.all([
          dataService.getVendors(),
          dataService.getProducts()
        ]);
        setVendors(Array.isArray(vendRes.data.data) ? vendRes.data.data : []);
        setProducts(Array.isArray(prodRes.data.data) ? prodRes.data.data : []);
      } catch (err) {
        console.error("Failed to fetch vendors/products", err);
      }
    };
    fetchData();
  }, []);

  const [formData, setFormData] = useState({
    purchaseId: `PO-${Math.floor(1000 + Math.random() * 9000)}`,
    poNumber: "",
    vendor: "",
    date: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: "",
    shippingAddress: "",
    status: "Draft",
    paymentTerms: "Net 30",
    destinationWarehouse: "Main Warehouse - dubai",
    notes: "",
    tax: 0,
    additionalCosts: {
      shipping: 0,
      importCosts: 0,
      others: 0
    }
  });

  const [items, setItems] = useState([
    { product: "", quantity: 1, price: 0, tax: 0, total: 0 }
  ]);

  useEffect(() => {
    if (isEditMode) {
      const fetchPurchase = async () => {
        try {
          const response = await dataService.getPurchases();
          const purchase = response.data.data.find(p => p._id === id);
          if (purchase) {
            setFormData({
              purchaseId: purchase.purchaseId || "",
              poNumber: purchase.poNumber || "",
              vendor: purchase.vendor || "",
              date: purchase.date ? new Date(purchase.date).toISOString().split('T')[0] : "",
              expectedDeliveryDate: purchase.expectedDeliveryDate ? new Date(purchase.expectedDeliveryDate).toISOString().split('T')[0] : "",
              shippingAddress: purchase.shippingAddress || "",
              status: purchase.status || "Draft",
              paymentTerms: purchase.paymentTerms || "Net 30",
              destinationWarehouse: purchase.destinationWarehouse || "Main Warehouse - dubai",
              notes: purchase.notes || "",
              tax: purchase.tax || 0,
              additionalCosts: purchase.additionalCosts || { shipping: 0, importCosts: 0, others: 0 }
            });
            setItems(purchase.items || [{ product: "", quantity: 1, price: 0, tax: 0, total: 0 }]);
          } else {
            setError("Purchase order not found");
          }
        } catch (err) {
          setError("Failed to fetch purchase order details");
        } finally {
          // done
        }
      };
      fetchPurchase();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("ship") || name === "importCosts" || name === "others") {
      setFormData(prev => ({
        ...prev,
        additionalCosts: { ...prev.additionalCosts, [name]: parseFloat(value) || 0 }
      }));
    } else {
      setFormData(prev => {
        const updated = { ...prev, [name]: value };
        // Auto-fill address if vendor is selected
        if (name === "vendor") {
          const selectedVend = vendors.find(v => v.name === value);
          if (selectedVend) {
            updated.shippingAddress = selectedVend.address || "";
          }
        }
        return updated;
      });
    }
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const newItems = [...items];

    if (name === "product") {
      newItems[index].product = value;
      const selectedProd = products.find(p => p.name === value);
      if (selectedProd) {
        newItems[index].price = selectedProd.costPrice || 0;
        newItems[index].tax = selectedProd.taxRate || 0;
        // recalculate row total
        const qty = newItems[index].quantity;
        const price = newItems[index].price;
        const tax = newItems[index].tax;
        newItems[index].total = (qty * price) + ((qty * price * tax) / 100);
      }
    } else {
      newItems[index][name] = name === "product" ? value : parseFloat(value) || 0;
      // Calculate row total
      const qty = newItems[index].quantity;
      const price = newItems[index].price;
      const tax = newItems[index].tax;
      newItems[index].total = (qty * price) + ((qty * price * tax) / 100);
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product: "", quantity: 1, price: 0, tax: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateGrandTotal = () => {
    const itemsTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const extra = (parseFloat(formData.additionalCosts.shipping) || 0) + (parseFloat(formData.additionalCosts.importCosts) || 0) + (parseFloat(formData.additionalCosts.others) || 0);
    return itemsTotal + extra;
  };

  const calculateTotalTax = () => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const taxRate = parseFloat(item.tax) || 0;
      return sum + ((qty * price * taxRate) / 100);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const payload = {
      ...formData,
      items,
      tax: calculateTotalTax(),
      amount: calculateGrandTotal()
    };

    try {
      if (isEditMode) {
        await dataService.updatePurchase(id, payload);
        addToast("Purchase order updated successfully", "success");
      } else {
        await dataService.addPurchase(payload);
        addToast("Purchase order created successfully", "success");
      }
      navigate("/purchases");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create purchase order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-vendor-page">
      <div className="page-header">
        <h1>{isEditMode ? "Edit Purchase Order" : "Create Purchase Order"}</h1>
        <p>{isEditMode ? `Updating ${formData.purchaseId}` : "Create a new purchase order for vendors"}</p>
      </div>

      {error && <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="vendor-grid">
          <div className="vendor-left">
            <div className="vendor-card">
              <h2>Purchase Order Details</h2>
              <div className="v-name">
                <div>
                  <p>PO Identification</p>
                  <input name="purchaseId" value={formData.purchaseId} onChange={handleChange} required placeholder="PO-xxx" />
                </div>
                <div>
                  <p>PO Number (Internal)</p>
                  <input name="poNumber" value={formData.poNumber} onChange={handleChange} placeholder="Ref-xxx" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <SearchableSelect
                    label="Vendor Name/ID"
                    name="vendor"
                    options={vendors}
                    value={formData.vendor}
                    onChange={handleChange}
                    placeholder="Search/Select a Vendor"
                    addPath="/vendors/add"
                    addLabel="+ New Vendor"
                  />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Order Date</p>
                  <input type="date" name="date" value={formData.date} onChange={handleChange} required />
                </div>
                <div>
                  <p>Expected Delivery Date</p>
                  <input type="date" name="expectedDeliveryDate" value={formData.expectedDeliveryDate} onChange={handleChange} />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Shipping Address</p>
                  <input name="shippingAddress" value={formData.shippingAddress} onChange={handleChange} placeholder="enter shipping address" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Order Items</h2>
              {items.map((item, index) => (
                <div key={index} className="v-name" style={{ marginBottom: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 2 }}>
                    <SearchableSelect
                      label="Product"
                      name="product"
                      options={products}
                      value={item.product}
                      onChange={(e) => handleItemChange(index, e)}
                      placeholder="Search Product"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Qty</p>
                    <input type="number" name="quantity" value={item.quantity} onChange={(e) => handleItemChange(index, e)} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Unit Cost</p>
                    <input type="number" name="price" value={item.price} onChange={(e) => handleItemChange(index, e)} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Tax %</p>
                    <input type="number" name="tax" value={item.tax} onChange={(e) => handleItemChange(index, e)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p>Total</p>
                    <input value={item.total.toFixed(2)} disabled />
                  </div>
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(index)} style={{ padding: '8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>×</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addItem} className="secondary-btn" style={{ marginTop: '10px' }}>+ Add Item</button>
            </div>

            <div className="vendor-card">
              <h2>Additional Costs</h2>
              <div className="v-name">
                <div>
                  <p>Shipping Cost</p>
                  <input type="number" name="shipping" value={formData.additionalCosts.shipping} onChange={handleChange} />
                </div>
                <div>
                  <p>Import Costs</p>
                  <input type="number" name="importCosts" value={formData.additionalCosts.importCosts} onChange={handleChange} />
                </div>
                <div>
                  <p>Other Costs</p>
                  <input type="number" name="others" value={formData.additionalCosts.others} onChange={handleChange} />
                </div>
              </div>
            </div>
          </div>

          <div className="vendor-right">
            <div className="vendor-card">
              <div className="v-namer">
                <h2>Order Summary</h2>
                <div>
                  <p>Grand Total</p>
                  <input value={`AED ${calculateGrandTotal().toFixed(2)}`} disabled style={{ fontSize: '1.2rem', fontWeight: 'bold' }} />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Purchase Settings</h2>
                <div>
                  <p>Order Status</p>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="Draft">Draft</option>
                    <option value="Pending">Pending</option>
                    <option value="Confirmed">Confirmed</option>
                  </select>
                  <p>Payment Terms</p>
                  <select name="paymentTerms" value={formData.paymentTerms} onChange={handleChange}>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 60">Net 60</option>
                  </select>
                  <p>Destination Warehouse</p>
                  <select name="destinationWarehouse" value={formData.destinationWarehouse} onChange={handleChange}>
                    <option value="Main Warehouse - dubai">Main Warehouse - dubai</option>
                    <option value="West Coast Hub - sharjah">West Coast Hub - sharjah</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Notes</h2>
              <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Add any notes or special instructions" rows={5} style={{ width: '100%', borderRadius: '6px', border: '1px solid #ddd', padding: '10px' }}></textarea>
            </div>

            <div className="vendor-actions">
              <button type="button" className="secondary-btn" onClick={() => navigate("/purchases")}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Saving..." : (isEditMode ? "Update Purchase Order" : "Create Purchase Order")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreatePurchase;
